<script setup>
/* Auswahl eines Vereins aus dem Klubregister — KEIN Freitext.
 *
 * Eingeklappt: ein Feld mit Logo und Name des gewählten Vereins. Aufgeklappt: Suchfeld und Liste mit Logo
 * je Eintrag (höchstens MAX_SHOWN Treffer sichtbar, die Bilder laden erst, wenn die Zeile eingehängt wird —
 * Egress, Konzept Abschnitt 3; mehr Treffer erscheinen beim Eintippen). Die Liste klappt im Fluss auf (nicht als
 * schwebendes Fenster), damit sie in Dialogen und auf dem Handy nicht abgeschnitten wird.
 *
 * Fehlt ein Verein: Hinweis, den Admin zu bitten; für admin ein Knopf, der das Klubregister öffnet.
 *
 * Verein im Register gewählt -> 'update:modelValue' mit der Id und 'select' mit dem ganzen Verein (der Aufrufer
 * trägt Verweis UND Text ein, domain/footballClub.js stampClub). Ist am Datensatz noch ein alter Text ohne
 * Verweis (Altbestand), zeigt das Feld ihn mit dem Hinweis "noch nicht im Register".
 *
 * readonly: nur Anzeige (staff, guest oder Ansicht ohne Bearbeiten). Die Rechte prüft der Aufrufer; die
 * Datenbank erzwingt sie ohnehin.
 */
import { ref, computed, watch } from 'vue';
import { useFootballClubs } from '../composables/useFootballClubs.js';
import { useAuth } from '../composables/useAuth.js';
import { filterClubs, resolveClub } from '../domain/footballClub.js';
import ClubLogo from './ClubLogo.vue';

const MAX_SHOWN = 40;

const props = defineProps({
  label: { type: String, required: true },
  /* Alter Text ohne Verweis (Altbestand) — nur zur Anzeige */
  legacyName: { type: String, default: '' },
  legacyLogoPath: { type: String, default: null },
  readonly: { type: Boolean, default: false },
  /* Liste von Anfang an aufgeklappt (Komponententests ohne Klick; sonst nicht benutzt) */
  defaultOpen: { type: Boolean, default: false },
  /* Dieser Verein steht nicht zur Wahl (Zusammenführen: nicht mit sich selbst) */
  exclude: { type: String, default: null },
  /* "Kein Verein" wählbar */
  clearable: { type: Boolean, default: true },
});
const modelValue = defineModel({ type: String, default: null });
const emit = defineEmits(['select']);

const { sorted, byId, loaded, loading, error, ensureLoaded, registerDialogOpen } = useFootballClubs();
const { role } = useAuth();
ensureLoaded();

const open = ref(props.defaultOpen);
const query = ref('');
const search = ref(null);

const shown = computed(() => filterClubs(sorted.value.filter((c) => c.id !== props.exclude), query.value));
const visible = computed(() => shown.value.slice(0, MAX_SHOWN));
const current = computed(() => resolveClub(byId.value, modelValue.value, props.legacyName, props.legacyLogoPath));
const hasValue = computed(() => !!(current.value.name || current.value.logoPath));
const isLegacy = computed(() => hasValue.value && !current.value.fromRegister && !modelValue.value);

watch(open, (isOpen) => {
  if (isOpen) {
    query.value = '';
    ensureLoaded();
    setTimeout(() => search.value && search.value.focus(), 0);
  }
});

function pick(club) {
  modelValue.value = club ? club.id : null;
  emit('select', club || null);
  open.value = false;
}

function openRegister() {
  open.value = false;
  registerDialogOpen.value = true;
}
</script>

<template>
  <div class="club-select" :class="{ open }">
    <label class="cs-label">{{ label }}</label>

    <!-- Nur Anzeige -->
    <span v-if="readonly" class="cs-line">
      <ClubLogo :path="current.logoPath" :size="22" />
      {{ current.name || '—' }}
    </span>

    <template v-else>
      <button
        type="button"
        class="cs-field"
        :aria-expanded="open"
        aria-haspopup="listbox"
        @click="open = !open"
      >
        <ClubLogo :path="current.logoPath" :size="24" />
        <span v-if="hasValue" class="cs-name">{{ current.name }}</span>
        <span v-else class="cs-placeholder">{{ $t('clubSelect.placeholder') }}</span>
        <svg class="cs-caret" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
      <p v-if="isLegacy" class="cs-legacy">{{ $t('clubSelect.notInRegister') }}</p>

      <div v-if="open" class="cs-panel" @keydown.esc.stop="open = false">
        <input
          ref="search"
          v-model="query"
          type="text"
          class="cs-search"
          :placeholder="$t('clubSelect.search')"
          :aria-label="$t('clubSelect.search')"
        />

        <p v-if="loading && !loaded" class="cs-note">{{ $t('common.loading') }}</p>
        <p v-else-if="error && !loaded" class="cs-note cs-bad">{{ $t('errors.loadFailed') }}</p>

        <ul v-else class="cs-list" role="listbox" :aria-label="label">
          <li v-if="clearable">
            <button type="button" class="cs-item cs-none" role="option" :aria-selected="!hasValue" @click="pick(null)">
              {{ $t('clubSelect.none') }}
            </button>
          </li>
          <li v-for="club in visible" :key="club.id">
            <button
              type="button"
              class="cs-item"
              role="option"
              :aria-selected="club.id === modelValue"
              :class="{ active: club.id === modelValue }"
              @click="pick(club)"
            >
              <ClubLogo :path="club.logoPath" :size="24" />
              <span class="cs-name">{{ club.name }}</span>
            </button>
          </li>
        </ul>
        <p v-if="shown.length > MAX_SHOWN" class="cs-note">{{ $t('clubSelect.more', { count: shown.length - MAX_SHOWN }) }}</p>
        <p v-if="loaded && !shown.length" class="cs-note">{{ $t('clubSelect.noMatch') }}</p>

        <div v-if="loaded" class="cs-missing">
          <span>{{ $t('clubSelect.missing') }}</span>
          <button v-if="role === 'admin'" type="button" class="btn ghost small" @click="openRegister">
            {{ $t('clubSelect.openRegister') }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.club-select {
  grid-column: 1 / -1;
  min-width: 0;
}
.cs-label {
  display: block;
  font-size: var(--fs-2xs);
  color: var(--dim);
  text-transform: uppercase;
  margin-bottom: 4px;
}
.cs-line {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.cs-field {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 40px;
  padding: 6px 10px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.cs-field:hover,
.open .cs-field {
  border-color: var(--green);
}
.cs-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cs-placeholder {
  flex: 1;
  color: var(--dim);
}
.cs-caret {
  flex: none;
  color: var(--muted);
}
.cs-legacy {
  margin: 4px 0 0;
  font-size: var(--fs-xs);
  color: var(--amber);
}
.cs-panel {
  margin-top: 6px;
  padding: 8px;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 8px;
}
.cs-search {
  width: 100%;
  margin-bottom: 6px;
  padding: 7px 9px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text);
  font: inherit;
  font-size: var(--fs-sm);
}
.cs-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 240px;
  overflow-y: auto;
}
.cs-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 40px;
  padding: 4px 8px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: var(--text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.cs-item:hover {
  background: var(--card-hover);
}
.cs-item.active {
  background: var(--card);
  color: var(--green);
}
.cs-none {
  color: var(--muted);
}
.cs-note {
  margin: 6px 0 0;
  font-size: var(--fs-xs);
  color: var(--muted);
}
.cs-bad {
  color: var(--red);
}
.cs-missing {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-soft);
  font-size: var(--fs-xs);
  color: var(--muted);
}
</style>
